import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import { Add, Refresh, Delete, CloudUpload } from '@material-ui/icons';
import { BackButton } from '../../sc-utilities';
import MaterialTable from 'material-table';
import { hasPermission, localization, getDimensionsOfPagination } from '../../sc-utilities';
import SKUMappingDelete from './SKUMappingDelete';
import * as actions from './store/skuMappingActions';
import * as action from '../../np-core/Auth/store/authActions';
import Toolbar from '../../sc-utilities/Toolbar';
import { MuiTableRows } from '../../../themes';
import Export from '../../sc-components/ExportForm/';
import * as Yup from 'yup';

const styles = (theme) => ({
    '@global': {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: '40px',
            padding: '2px',
            fontSize: '0.7rem',
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];
const tableRef = React.createRef();

const SKUMappingList = (props) => {
    const {
        classes,
        deleting,
        translations,
        permissions,
        onFetchSKUMappings,
        onFetchSKUMappingsCount,
        fetching,
        saveColumnsFilter,
        activeCustomer,
        onBulkDeleteSKUMappings,
        tableColumns,
        redirectHandler,
    } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, skuMappingIDs: [] });
    const columnObject = useMemo(
        () => tableColumns,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const selectionPermissions = hasPermission(permissions, 'sku_mapping_delete');

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: true,
        selection: selectionPermissions,
        selectionProps: (rowData) => {
            let disabled = !selectionPermissions;
            rowData.tableData.disabled = disabled;
            return disabled
                ? {
                      disabled: true,
                      checked: false,
                  }
                : {};
        },
        search: false,
        minBodyHeight: window.innerHeight - 290 < 300 ? 300 : window.innerHeight - 290,
        maxBodyHeight: window.innerHeight - 290,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false,
    });

    const filterQuery = (filters) => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomer;
        query['q'] = q;
        let count = await onFetchSKUMappingsCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count);
            query['pageSize'] = dimensions.pageSize;
            rowsPageSizeInit = dimensions.rowsPageSize;
            pageSize = dimensions.pageSize;
        }
        let result = count ? await onFetchSKUMappings(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const initialValues = [
        'Vendor',
        'Model',
        'ProductIdentifier',
        'Storage',
        'Memory',
        'CPU',
        'GPU',
        'Grading',
        'Color',
        'Carrier',
        'SKU',
    ];

    const tableTitle = () => {
        return (
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations['sku_mappings'] || 'SKU Mappings'}</Typography>
                {hasPermission(permissions, 'sku_mapping_create') ? (
                    <Fab
                        id="create_sku_mapping"
                        variant="extended"
                        style={{ borderRadius: '1px' }}
                        size="small"
                        color="primary"
                        aria-label="Add"
                        className={classes.fab}
                        onClick={() => redirectHandler(`/settings/sku-mapping/create`)}
                    >
                        <Add size="small" />
                        {translations['create_new'] || 'Create new'}
                    </Fab>
                ) : null}
                {hasPermission(permissions, 'sku_mapping_export') ? (
                    <Export
                        route={'sku-mappings'}
                        tableRef={tableRef}
                        initialValues={initialValues}
                        formSettings={{
                            Validation: initialValues.reduce((o, k) => ({ ...o, [k]: Yup.boolean() }), {}),
                            ButtonSubmit: hasPermission(permissions, 'sku_mapping_export')
                                ? {
                                      Title: translations['download'] || 'Download',
                                      Color: 'primary',
                                  }
                                : null,
                        }}
                        showVisibilities={false}
                        showTypes={false}
                        preSelectAll={true}
                    />
                ) : null}
                {hasPermission(permissions, 'sku_mapping_import') ? (
                    <Tooltip title={translations['import_sku_mappings'] || 'Import SKU mappings'}>
                        <IconButton
                            aria-label={translations['import_sku_mappings'] || 'Import SKU mappings'}
                            value="csv"
                            onClick={() => redirectHandler(`/settings/sku-mapping/upload`)}
                        >
                            <CloudUpload />
                        </IconButton>
                    </Tooltip>
                ) : null}
                <Tooltip title={translations['refresh'] || 'Refresh'}>
                    <IconButton
                        aria-label={translations['refresh'] || 'Refresh'}
                        component="div"
                        disabled={fetching}
                        onClick={() => tableRef.current && tableRef.current.onQueryChange()}
                    >
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        );
    };

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, skuMappingIDs: [] });
    };

    const handleCloseDanger = async () => {
        await onBulkDeleteSKUMappings({
            SKUMappingIDs: deleteModalState.skuMappingIDs,
            ActiveCustomerID: activeCustomer,
        });
        setDeleteModalState({ open: false, skuMappingIDs: [] });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleBulkDelete = (skuMappingIDs) => {
        setDeleteModalState({ ...deleteModalState, open: true, skuMappingIDs: skuMappingIDs });
    };

    const tableActions = [
        {
            tooltip: `${translations['remove_all_selected'] || 'Remove all selected'} ${
                translations['sku_mappings'] || 'SKU Mappings'
            }`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map((skuMapping) => skuMapping.SKUMappingID)),
        },
    ];

    const columnHidden = (newColumnState) => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map((column) => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        });
        objToSendColumn[`SKUMapping`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomer });
        return newColumnState;
    };

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: (props) => (
                        <Toolbar
                            data={props}
                            actions={tableActions}
                            selectedRows={props.selectedRows}
                            translations={translations}
                        />
                    ),
                }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size;
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {
                    if (tableRef?.current?.tableContainerDiv?.current) {
                        tableRef.current.tableContainerDiv.current.scrollTop = 0;
                    }
                }}
            />
            {hasPermission(permissions, 'sku_mapping_delete') ? (
                <SKUMappingDelete
                    activeCustomer={activeCustomer}
                    entityName={translations['sku_mappings'] || 'SKU Mappings'}
                    translations={translations}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleting={deleting}
                    deleteModalState={deleteModalState}
                />
            ) : null}
        </Paper>
    );
};

const mapStateToProps = (state) => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations:
            state.translate && state.translate.translations
                ? state.translate.translations.activeTranslations || {}
                : {},
        permissions: state?.core?.login?.permissions || [],
        deleting: state.setting.skuMappings.deleting,
        fetching: state.setting.skuMappings.fetching,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        onFetchSKUMappingsCount: (filter) => dispatch(actions.fetchSKUMappingCount(filter)),
        onFetchSKUMappings: (filter) => dispatch(actions.fetchSKUMapping(filter)),
        onBulkDeleteSKUMappings: (skuMapping) => dispatch(actions.bulkDeleteSKUMappings(skuMapping)),
        saveColumnsFilter: (data) => dispatch(action.saveColumnsFilter(data)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles, { withTheme: true })(SKUMappingList));
